/**
 * Shrinks a camera photo on the phone to roughly 200 KB before upload (SPEC §5) using the
 * browser's own canvas (no compression library). Re-encoding to JPEG also drops EXIF; the
 * server strips and re-encodes again regardless (RULES.md rule 6).
 */
const MAX_EDGE = 1600;
const TARGET_BYTES = 200 * 1024;
const QUALITIES = [0.82, 0.72, 0.62, 0.52];

export async function compressPhoto(file: Blob): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // Formats the browser cannot decode (e.g. HEIC on Android) go up as-is; the server
    // converts them.
    return file;
  }
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  let best: Blob | null = null;
  for (const q of QUALITIES) {
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', q));
    if (!blob) break;
    best = blob;
    if (blob.size <= TARGET_BYTES) break;
  }
  return best ?? file;
}
